// packages/web-ui/src/AppShell/useBreakpoint.ts
//
// W2-S2 AC #3 — `useBreakpoint()` hook. Listens to `window.resize` (debounced
// 100 ms) and returns the current named breakpoint. Uses React 19's native
// `useSyncExternalStore` for SSR-safe subscription semantics.

import { useSyncExternalStore } from 'react';
import { getBreakpoint, type Breakpoint } from './responsive-tokens.js';

const RESIZE_DEBOUNCE_MS = 100;

type Listener = () => void;

/** Module-level store — single resize listener shared across all hook consumers. */
class BreakpointStore {
  private listeners = new Set<Listener>();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private windowListenerAttached = false;
  private cachedWidth: number | null = null;

  subscribe = (listener: Listener): (() => void) => {
    if (typeof window === 'undefined') {
      // SSR / non-browser environments — return a no-op unsubscribe.
      return () => undefined;
    }
    this.listeners.add(listener);
    if (!this.windowListenerAttached) {
      window.addEventListener('resize', this.handleResize);
      this.windowListenerAttached = true;
    }
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0 && this.windowListenerAttached) {
        window.removeEventListener('resize', this.handleResize);
        this.windowListenerAttached = false;
        if (this.debounceTimer !== null) {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = null;
        }
        this.cachedWidth = null;
      }
    };
  };

  getSnapshot = (): Breakpoint => {
    const width = typeof window === 'undefined' ? 1280 : window.innerWidth;
    // Cache the width so successive calls in the same render pass return the
    // same Breakpoint reference (useSyncExternalStore equality contract).
    if (this.cachedWidth === width) {
      return getBreakpoint(width);
    }
    this.cachedWidth = width;
    return getBreakpoint(width);
  };

  /** SSR snapshot — defaults to `desktop-xl` when `window` is unavailable. */
  getServerSnapshot = (): Breakpoint => 'desktop-xl';

  private handleResize = (): void => {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.cachedWidth = null;
      this.listeners.forEach((listener) => {
        listener();
      });
    }, RESIZE_DEBOUNCE_MS);
  };
}

const breakpointStore = new BreakpointStore();

/**
 * React hook returning the current viewport breakpoint. Re-renders the host
 * component on debounced (100 ms) resize.
 */
export function useBreakpoint(): Breakpoint {
  return useSyncExternalStore(
    breakpointStore.subscribe,
    breakpointStore.getSnapshot,
    breakpointStore.getServerSnapshot,
  );
}
